import { z } from "zod";

/**
 * Zod schemas for `bu_instance` (Coach Manager BU connection configs).
 *
 * `password` is intentionally never returned by any API response (see
 * src/api/bu-instances.ts) — the same form drives both create and edit, so on
 * edit an empty/omitted `password` means "keep the existing credential
 * unchanged," and a non-empty value re-encrypts and overwrites it.
 */

export const buInstanceFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(200, "Name is too long"),
  endpointUrl: z.string().min(1, "Endpoint URL is required").url("Must be a valid URL"),
  username: z.string().min(1, "Username is required").max(200, "Username is too long"),
  password: z.string().max(500, "Password is too long").optional(),
  // Coach Manager's fixed CompanyID for this BU — only required when this BU
  // is used as a copy *target* (see src/lib/coach-manager/transform.ts), so
  // stays optional here rather than blocking BU creation before it's known.
  companyId: z.string().max(200, "Too long").optional(),
  active: z.boolean(),
});

export type BuInstanceFormValues = z.infer<typeof buInstanceFormSchema>;

// What the API accepts on POST — password required.
export const createBuInstanceSchema = z.object({
  name: z.string().min(1).max(200),
  endpointUrl: z.string().min(1).url(),
  username: z.string().min(1).max(200),
  password: z.string().min(1).max(500),
  companyId: z.string().max(200).optional(),
  active: z.boolean().default(true),
});

// PUT is a partial update — every field optional. Omit `password` (or send
// it undefined) to keep the existing credential; a non-empty string is
// re-encrypted, but an explicit empty string fails validation rather than
// silently clearing the credential.
export const updateBuInstanceSchema = createBuInstanceSchema.partial();

export type CreateBuInstanceInput = z.infer<typeof createBuInstanceSchema>;
export type UpdateBuInstanceInput = z.infer<typeof updateBuInstanceSchema>;

// "Test Connection" probe body — works for both an unsaved create-time test
// and an edit-time test with a freshly-typed password.
export const testConnectionSchema = z.object({
  endpointUrl: z.string().min(1).url(),
  username: z.string().min(1),
  password: z.string().min(1),
});
